import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errText } from "@/lib/api";

function IFPIllustration() {
  return (
    <div className="relative mx-auto w-full max-w-[620px]">
      <div className="absolute inset-x-12 bottom-4 h-20 rounded-full bg-cyan-300/20 blur-3xl" />
      <div className="relative mx-auto w-[86%] max-w-[560px]">
        <div className="relative aspect-[16/9] overflow-hidden rounded-[18px] border-[10px] border-slate-900/90 bg-slate-950 shadow-2xl ring-1 ring-white/20">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-blue-950" />
          <div className="absolute left-[7%] top-[12%] h-[58%] w-[40%] rounded-xl border border-white/10 bg-white/[0.06] p-5">
            <div className="mb-4 h-2.5 w-28 rounded-full bg-cyan-300/80" />
            <div className="space-y-3">
              <div className="h-2 w-full rounded-full bg-white/20" />
              <div className="h-2 w-4/5 rounded-full bg-white/15" />
              <div className="h-2 w-3/5 rounded-full bg-white/10" />
            </div>
            <div className="mt-6 grid grid-cols-3 gap-2">
              <div className="h-12 rounded-lg bg-blue-400/20" />
              <div className="h-12 rounded-lg bg-cyan-300/20" />
              <div className="h-12 rounded-lg bg-white/10" />
            </div>
          </div>
          <div className="absolute right-[8%] top-[16%] h-12 w-12 rounded-full border-4 border-cyan-200/60 bg-cyan-300/20 shadow-lg" />
          <div className="absolute bottom-[12%] right-[10%] h-20 w-28 rounded-xl border border-white/10 bg-white/[0.08]" />
          <div className="absolute bottom-[9%] left-[8%] rounded-full bg-white/10 px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.2em] text-blue-100">
            Smartboard / IFP
          </div>
        </div>
        <div className="mx-auto h-4 w-24 rounded-b-xl bg-slate-900/90" />
        <div className="mx-auto h-2 w-44 rounded-full bg-slate-900/70" />
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email.trim(), password);
      nav("/");
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0f17] flex items-stretch">
          <div className="relative hidden overflow-hidden lg:flex lg:w-[58%] items-center bg-gradient-to-br from-[#082b78] via-[#1547c8] to-[#2388ff] p-10 xl:p-14">
          <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute right-[-80px] bottom-[-90px] h-80 w-80 rounded-full bg-cyan-300/10 blur-3xl" />
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
          
          <div className="relative z-10 flex h-full w-full flex-col">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20 font-display text-xl font-extrabold login-hero-text shadow-lg">
                P
              </div>
              <div>
                <div className="font-display text-lg font-extrabold tracking-tight login-hero-text">PYX KORDA</div>
                <div className="text-[10px] uppercase tracking-[0.28em] login-hero-subtle">Control Center</div>
              </div>
            </div>

            <div className="mt-12 max-w-xl">
              <div className="mb-3 inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] login-hero-text backdrop-blur">
                Smart Education Project
              </div>
              <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight login-hero-text xl:text-5xl">
                IFP Installation
                <span className="block login-hero-accent">Management System</span>
              </h1>
              <p className="mt-4 max-w-lg text-sm leading-6 login-hero-subtle">
                Pusat monitoring instalasi Smartboard/IFP, teknisi, jadwal, BAPP dan progres pekerjaan di seluruh wilayah.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {["Plan", "Install", "Monitor", "Report"].map((t) => (
                  <span key={t} className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold login-hero-text backdrop-blur">
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-auto flex items-end justify-center">
              <IFPIllustration />
            </div>

            <div className="mt-5 flex items-center justify-between text-[11px] login-hero-subtle">
              <span>PYX Smart Education</span>
              <span>Installation • Monitoring • Reporting</span>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-1 items-center justify-center bg-white px-6 py-10 lg:w-[42%] lg:px-12 xl:px-16">
          <div className="w-full max-w-md">
            <div className="mb-10 lg:hidden">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 font-display font-extrabold text-white">P</div>
                <span className="font-display font-extrabold text-slate-900">PYX KORDA</span>
              </div>
            </div>

            <div className="mb-8">
              <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700">👋</div>
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Selamat Datang</h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                Silakan masuk menggunakan akun operasional Anda untuk mengakses sistem.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Email</label>
                <input
                  data-testid="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                  placeholder="nama@pyx.id"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Password</label>
                <input
                  data-testid="login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                  placeholder="Masukkan password Anda"
                />
              </div>
              {error && (
                <div data-testid="login-error" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-600">
                  {error}
                </div>
              )}
              <button
                data-testid="login-submit"
                disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-700 to-blue-600 px-4 py-3.5 text-sm font-bold login-hero-button-text shadow-lg shadow-blue-200 transition-all hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Masuk ke Control Center
              </button>
            </form>

            <div className="mt-8 rounded-xl border border-blue-100 bg-blue-50/70 p-4 text-xs leading-5 text-slate-500">
              <span className="font-semibold text-blue-700">Akses terbatas.</span> Sistem ini hanya dapat digunakan oleh pengguna yang berwenang.
            </div>

            <div className="mt-8 flex items-center justify-between text-[10px] !text-slate-400">
              <span>© 2026 PYX KORDA</span>
              <span>IFP Installation Management</span>
            </div>
          </div>
        </div>
      </div>
  );
}